// src/api/instructorsService.js
import apiClientService from "./ApiClientService";
import { buildMonthRange } from "./reservationsService";
import {
   blackoutDebug,
   explainBlackoutDateTime,
} from "../calendar/debug/blackoutDebug";

/* =========================
   Instructors CRUD
========================= */
export async function getInstructors() {
   const res = await apiClientService.get("/instructors");
   if (!res.ok) throw new Error(await res.text());
   return res.json();
}

export async function createInstructors(payload) {
   const res = await apiClientService.post("/instructors", payload);
   if (!res.ok) throw new Error(await res.text());
   return res.json();
}

export async function patchInstructors(id, payload) {
   const res = await apiClientService.patch(`/instructors/${id}`, payload);
   if (!res.ok) throw new Error(await res.text());
   return res.json();
}

export async function patchInstructorOrder(id, order) {
   // Trimitem obiect simplu; ApiClientService se ocupă de JSON & headers
   return patchInstructors(id, { order });
}

export async function deleteInstructors(id) {
   const res = await apiClientService.delete(`/instructors/${id}`);
   if (!res.ok) throw new Error(await res.text());
   return true;
}

/* =========================
   Blackouts (Indisponibilități)
   API: one slot = one dateTime (single hour). No REPEAT ranges.
========================= */

function normalizeBlackoutInstructorId(value) {
   if (value === null || value === undefined) return null;
   const text = String(value).trim();
   if (!text) return null;
   if (/^\d+$/.test(text)) {
      const numeric = Number(text);
      if (Number.isFinite(numeric) && numeric > 0) {
         return Math.trunc(numeric);
      }
   }
   return text;
}

/** Normalize to ISO-8601 datetime string (keeps wall-clock Z form when already a string). */
function asBlackoutDateTime(value) {
   if (!value) return undefined;
   if (typeof value === "string") {
      const raw = value.trim();
      return raw || undefined;
   }
   if (value instanceof Date) {
      return Number.isNaN(value.getTime()) ? undefined : value.toISOString();
   }
   const parsed = new Date(value);
   return Number.isNaN(parsed.getTime()) ? undefined : parsed.toISOString();
}

/**
 * POST /instructors/blackouts
 * Body: { blackouts: [{ dateTime, instructorId }] }
 * Each entry blocks exactly one hour slot.
 */
export async function addInstructorBlackouts(input) {
   const payload = Array.isArray(input) ? { blackouts: input } : input || {};
   const seen = new Set();
   const out = [];

   for (const entry of payload.blackouts || []) {
      const instructorId = normalizeBlackoutInstructorId(
         entry?.instructorId ??
            entry?.instructor_id ??
            entry?.instructorID ??
            entry?.instructor?.id ??
            null,
      );
      if (instructorId == null) continue;

      const dateTime =
         asBlackoutDateTime(entry?.dateTime) ||
         asBlackoutDateTime(entry?.startDateTime) ||
         asBlackoutDateTime(entry?.start);
      if (!dateTime) continue;

      const key = `${String(instructorId)}|${dateTime}`;
      if (seen.has(key)) continue;
      seen.add(key);

      out.push({ instructorId, dateTime });
   }

   if (out.length === 0) return true;

   const res = await apiClientService.post("/instructors/blackouts", {
      blackouts: out,
   });
   blackoutDebug("API:POST /instructors/blackouts", {
      count: out.length,
      sample: out.slice(0, 5).map((item) => ({
         ...item,
         explain: explainBlackoutDateTime(item.dateTime),
      })),
      status: res.status,
      ok: res.ok,
   });
   const text = await res.text();
   if (!res.ok) throw new Error(text || "Failed to add blackouts");

   try {
      return text ? JSON.parse(text) : true;
   } catch {
      return true;
   }
}

/** Helper: block a single instructor hour slot. */
export async function addInstructorBlackout(instructorId, dateTime) {
   return addInstructorBlackouts([{ instructorId, dateTime }]);
}

function widenEndDateParam(value) {
   const raw = String(value || "").trim();
   const m = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
   if (!m) return raw;

   const y = Number(m[1]);
   const mo = Number(m[2]) - 1;
   const d = Number(m[3]);
   const dt = new Date(y, mo, d);
   if (Number.isNaN(dt.getTime())) return raw;

   dt.setDate(dt.getDate() + 1);
   const yy = dt.getFullYear();
   const mm = String(dt.getMonth() + 1).padStart(2, "0");
   const dd = String(dt.getDate()).padStart(2, "0");
   return `${yy}-${mm}-${dd}`;
}

function toBlackoutYmd(value) {
   const raw = String(value || "").trim();
   if (!raw) return "";
   const direct = raw.match(/^(\d{4}-\d{2}-\d{2})/);
   if (direct?.[1]) return direct[1];
   const parsed = new Date(raw);
   if (Number.isNaN(parsed.getTime())) return "";
   const y = parsed.getFullYear();
   const m = String(parsed.getMonth() + 1).padStart(2, "0");
   const d = String(parsed.getDate()).padStart(2, "0");
   return `${y}-${m}-${d}`;
}

/** GET /instructors/blackouts/{instructorId}?startDate&endDate */
export async function getInstructorBlackouts(instructorId, opts = {}) {
   const id = encodeURIComponent(String(instructorId));

   const qs = new URLSearchParams();
   const startDate = toBlackoutYmd(opts?.startDate);
   const endDate = toBlackoutYmd(opts?.endDate);
   if (startDate) qs.set("startDate", startDate);
   if (endDate) qs.set("endDate", widenEndDateParam(endDate));

   const url =
      `/instructors/blackouts/${id}` + (qs.toString() ? `?${qs}` : "");

   const res = await apiClientService.get(url);
   if (!res.ok) throw new Error(await res.text());
   return res.json();
}

/** GET /instructors/blackouts?startDate&endDate */
export async function getInstructorsBlackouts(opts = {}) {
   const dateLike = opts?.date ?? opts?.monthDate ?? null;
   const monthRange = dateLike ? buildMonthRange(dateLike) : null;

   const startDate =
      toBlackoutYmd(opts?.startDate) ||
      toBlackoutYmd(opts?.startDateFrom) ||
      toBlackoutYmd(opts?.from) ||
      toBlackoutYmd(opts?.dateFrom) ||
      toBlackoutYmd(monthRange?.startDateFrom) ||
      toBlackoutYmd(dateLike);

   const endDate =
      toBlackoutYmd(opts?.endDate) ||
      toBlackoutYmd(opts?.startDateTo) ||
      toBlackoutYmd(opts?.to) ||
      toBlackoutYmd(opts?.dateTo) ||
      toBlackoutYmd(monthRange?.startDateTo) ||
      (dateLike
         ? toBlackoutYmd(
              new Date(
                 new Date(dateLike).getFullYear(),
                 new Date(dateLike).getMonth() + 1,
                 0,
              ),
           )
         : "");

   const qs = new URLSearchParams();
   if (startDate) qs.set("startDate", startDate);
   if (endDate) qs.set("endDate", widenEndDateParam(endDate));

   const url = `/instructors/blackouts` + (qs.toString() ? `?${qs}` : "");
   const res = await apiClientService.get(url);
   if (!res.ok) throw new Error(await res.text());
   const json = await res.json();
   const list = Array.isArray(json) ? json : json?.blackouts || json?.items || [];
   blackoutDebug("API:GET /instructors/blackouts", {
      startDate,
      endDate,
      count: Array.isArray(list) ? list.length : -1,
      sample: (Array.isArray(list) ? list : []).slice(0, 3).map((row) => ({
         id: row?.id,
         instructorId: row?.instructorId ?? row?.instructor_id,
         dateTime: row?.dateTime ?? row?.datetime,
         explain: explainBlackoutDateTime(row?.dateTime ?? row?.datetime),
      })),
   });
   return json;
}

/** GET /instructors-group/blackouts/{instructorsGroupId} */
export async function getInstructorsGroupBlackouts(instructorsGroupId) {
   const res = await apiClientService.get(
      `/instructors-group/blackouts/${Number(instructorsGroupId)}`,
   );
   if (!res.ok) throw new Error(await res.text());
   return res.json();
}

/** DELETE /instructors/blackouts/{id} */
export async function deleteInstructorBlackout(id) {
   const res = await apiClientService.delete(
      `/instructors/blackouts/${Number(id)}`,
   );
   if (!res.ok) throw new Error(await res.text());
   return true;
}

/** PATCH /instructors/blackouts/{id} — body: { dateTime } */
export async function updateInstructorBlackout(id, payload) {
   const dateTime = asBlackoutDateTime(
      payload?.dateTime ?? payload?.startDateTime ?? payload,
   );
   const body =
      dateTime != null
         ? { dateTime }
         : payload && typeof payload === "object"
           ? payload
           : {};

   const res = await apiClientService.patch(
      `/instructors/blackouts/${Number(id)}`,
      body,
   );
   if (!res.ok) throw new Error(await res.text());
   return res.json();
}
